import type { DeliverSchemaTypes } from "@bfc-software/api-schemas";
import {
  Button,
  EmptyState,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  Tag,
} from "@bfc-software/pallet";
// eslint-disable-next-line no-restricted-imports -- follow-up: use useCurrentLocalization()
import type { TFunction } from "i18next";

import { formatCount } from "../-data/format";

import { RouteStops } from "./route-stops";
import { ScanBar } from "./scan-bar";
import styles from "./routes-table.module.css";

export type DashRoute = DeliverSchemaTypes.components["schemas"]["DashRoute"];

const COLUMN_COUNT = 13;

/** Warehouse + route id: the service repeats a route id across warehouses. */
export function routeKey(row: DashRoute): string {
  return `${row.warehouse}:${row.routeId}`;
}

type Props = {
  rows: ReadonlyArray<DashRoute>;
  expanded: ReadonlySet<string>;
  onToggle: (key: string) => void;
  onOpen: (row: DashRoute) => void;
  lang: string;
  t: TFunction;
};

function isCompleted(row: DashRoute): boolean {
  return /complete/i.test(row.deliveryStatus);
}

function RouteRow({
  row,
  isExpanded,
  onToggle,
  onOpen,
  lang,
  t,
}: {
  row: DashRoute;
  isExpanded: boolean;
  onToggle: () => void;
  onOpen: () => void;
  lang: string;
  t: TFunction;
}) {
  const none = t("none");
  const n = (value: number) => formatCount(value, lang);

  return (
    <>
      {/* The whole row opens the route, as in the reference. The chevron is its
          own press target, so expanding a row does not also open the drawer. */}
      <TableRow data-test-role="route-row" onAction={onOpen}>
        <TableCell>
          <Button
            kind="icon-only"
            variant="bare"
            icon={isExpanded ? "ChevronDown" : "ChevronRight"}
            label={t(isExpanded ? "list.collapse" : "list.expand", {
              route: row.routeId,
            })}
            onPress={onToggle}
          />
        </TableCell>
        <TableCell>
          <div className={styles.stacked}>
            <span className={styles.primary}>{row.routeId}</span>
            <span className={styles.secondary}>{row.route}</span>
            {row.willcall ? (
              <Tag label={t("routes.willCall")} display={{ tone: "accent" }} />
            ) : null}
          </div>
        </TableCell>
        <TableCell>
          <div className={styles.stacked}>
            <span className={styles.primary}>{row.driverName || none}</span>
            <span className={styles.secondary}>{row.driver}</span>
          </div>
        </TableCell>
        <TableCell>{row.truck || none}</TableCell>
        <TableCell>
          <Tag
            label={row.deliveryStatus}
            display={{ tone: isCompleted(row) ? "success" : "info" }}
          />
        </TableCell>
        <TableCell>
          <span className={styles.nowrap}>{row.startTimestamp ?? none}</span>
        </TableCell>
        <TableCell>
          <span className={styles.nowrap}>{row.finishTimestamp ?? none}</span>
        </TableCell>
        <TableCell>
          {n(row.stopsDelivered)} / {n(row.stops)}
        </TableCell>
        <TableCell>{n(row.piecesDelivered)}</TableCell>
        <TableCell>{n(row.piecesScanned)}</TableCell>
        <TableCell>{n(row.piecesNonScanned)}</TableCell>
        <TableCell>
          <ScanBar value={row.scanPct} lang={lang} none={none} />
        </TableCell>
        <TableCell>{n(row.currentReturns)}</TableCell>
        <TableCell>
          {row.exceptionCount > 0 ? (
            <Tag
              label={t("routes.exceptions", { count: row.exceptionCount })}
              display={{ tone: "danger" }}
            />
          ) : (
            none
          )}
        </TableCell>
      </TableRow>
      {isExpanded ? (
        <TableRow data-test-role="route-detail-row">
          <TableCell colSpan={COLUMN_COUNT + 1}>
            <RouteStops
              warehouse={row.warehouse}
              routeId={row.routeId}
              lang={lang}
              t={t}
            />
          </TableCell>
        </TableRow>
      ) : null}
    </>
  );
}

export function RoutesTable({
  rows,
  expanded,
  onToggle,
  onOpen,
  lang,
  t,
}: Props) {
  return (
    <Table
      aria-label={t("routes.ariaLabel")}
      className={rows.length < 1 ? "min-h-full" : ""}
      isResizable={false}
    >
      <TableHead>
        <TableHeaderCell>
          <span className="sr-only">{t("list.expandAll")}</span>
        </TableHeaderCell>
        <TableHeaderCell isRowHeader={true}>
          {t("routes.route")}
        </TableHeaderCell>
        <TableHeaderCell>{t("routes.driver")}</TableHeaderCell>
        <TableHeaderCell>{t("routes.truck")}</TableHeaderCell>
        <TableHeaderCell>{t("routes.status")}</TableHeaderCell>
        <TableHeaderCell>{t("routes.started")}</TableHeaderCell>
        <TableHeaderCell>{t("routes.finished")}</TableHeaderCell>
        <TableHeaderCell>{t("routes.stops")}</TableHeaderCell>
        <TableHeaderCell>{t("routes.delivered")}</TableHeaderCell>
        <TableHeaderCell>{t("routes.scanned")}</TableHeaderCell>
        <TableHeaderCell>{t("routes.nonScanned")}</TableHeaderCell>
        <TableHeaderCell>{t("routes.scanPct")}</TableHeaderCell>
        <TableHeaderCell>{t("routes.returns")}</TableHeaderCell>
        <TableHeaderCell>{t("routes.issues")}</TableHeaderCell>
      </TableHead>
      <TableBody>
        {rows.length === 0 ? (
          <TableRow>
            <TableCell colSpan={COLUMN_COUNT + 1}>
              <EmptyState heading={t("list.empty")} />
            </TableCell>
          </TableRow>
        ) : (
          rows.map((row) => (
            <RouteRow
              key={routeKey(row)}
              row={row}
              isExpanded={expanded.has(routeKey(row))}
              onToggle={() => onToggle(routeKey(row))}
              onOpen={() => onOpen(row)}
              lang={lang}
              t={t}
            />
          ))
        )}
      </TableBody>
    </Table>
  );
}
